/**
 * @module components/AmbientMode/scenes/ComplianceScene
 * @description Escena de cumplimiento global para el modo ambient (25 segundos por rotación).
 *
 * Muestra en pantalla completa el estado de cumplimiento ISO con un diseño oscuro y minimalista:
 * - Donut SVG animado con el porcentaje de cumplimiento global
 * - Barras de progreso por categoría ISO (Organizacional, Personas, Física, Tecnológica)
 * - Contadores de controles completados, en progreso y pendientes
 *
 * Diseñada para proyectarse en monitores de sala de reuniones sin revelar información
 * sensible — usa datos agregados, no detalles de controles específicos.
 *
 * @see SceneEngine que monta esta escena según SCENE_ROTATION
 * @see useMetrics para los datos de cumplimiento agregados
 */
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useMetrics } from '../../../contexts/DataContext';
import { useData } from '../../../contexts/DataContext';

// ─── Donut SVG ────────────────────────────────────────────────────────────────

const DONUT_RADIUS = 130;
const DONUT_STROKE = 18;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

function AnimatedDonut({ rate, color }: { rate: number; color: string }) {
  const offset = DONUT_CIRCUMFERENCE - (rate / 100) * DONUT_CIRCUMFERENCE;
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 200);
    return () => clearTimeout(t);
  }, []);

  const cx = DONUT_RADIUS + DONUT_STROKE;
  const size = (DONUT_RADIUS + DONUT_STROKE) * 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Track */}
      <circle
        cx={cx} cy={cx} r={DONUT_RADIUS}
        fill="none"
        stroke="#1f2937"
        strokeWidth={DONUT_STROKE}
      />
      {/* Progress */}
      <circle
        cx={cx} cy={cx} r={DONUT_RADIUS}
        fill="none"
        stroke={color}
        strokeWidth={DONUT_STROKE}
        strokeLinecap="round"
        strokeDasharray={DONUT_CIRCUMFERENCE}
        strokeDashoffset={animated ? offset : DONUT_CIRCUMFERENCE}
        transform={`rotate(-90 ${cx} ${cx})`}
        style={{ transition: 'stroke-dashoffset 2.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
      />
    </svg>
  );
}

// ─── Animated counter ─────────────────────────────────────────────────────────

function useAnimatedCounter(target: number, duration = 2_500): number {
  const [value, setValue] = useState(0);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);

  return value;
}

// ─── Category bar ─────────────────────────────────────────────────────────────

interface CategoryBarProps {
  label: string;
  rate: number;
  total: number;
  completed: number;
  color: string;
  delay: number;
}

function CategoryBar({ label, rate, total, completed, color, delay }: CategoryBarProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm text-gray-400 w-52 truncate shrink-0">{label}</span>
      <div className="flex-1 h-3 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: mounted ? `${rate}%` : '0%',
            backgroundColor: color,
            transition: 'width 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </div>
      <span className="text-sm text-gray-500 w-24 text-right shrink-0">
        {completed}/{total}
      </span>
    </div>
  );
}

// ─── Main scene ──────────────────────────────────────────────────────────────

const CATEGORY_COLORS = [
  '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b',
  '#ef4444', '#06b6d4', '#ec4899', '#84cc16',
];

export const ComplianceScene = memo(function ComplianceScene() {
  const metrics = useMetrics();
  const { filteredControls } = useData();

  const rate = Math.round(metrics.controls.completionRate);
  const taskRate = Math.round(metrics.tasks.completionRate);
  const animatedRate = useAnimatedCounter(rate);

  // Agrupar controles por categoría
  const categories = useMemo(() => {
    const map = new Map<string, { total: number; completed: number }>();
    filteredControls.forEach(c => {
      const key = c.category ?? 'Sin categoría';
      const entry = map.get(key) ?? { total: 0, completed: 0 };
      entry.total++;
      if (c.status === 'completed') entry.completed++;
      map.set(key, entry);
    });
    return Array.from(map.entries())
      .map(([label, { total, completed }]) => ({
        label,
        total,
        completed,
        rate: total > 0 ? Math.round((completed / total) * 100) : 0,
      }))
      .sort((a, b) => b.rate - a.rate)
      .slice(0, 8);
  }, [filteredControls]);

  const donutColor = rate >= 80 ? '#10b981' : rate >= 60 ? '#3b82f6' : rate >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex h-full w-full max-w-7xl mx-auto px-12 py-10 gap-16 items-center">

      {/* Left — Donut + KPIs */}
      <div className="flex flex-col items-center gap-8 shrink-0">
        {/* Donut */}
        <div className="relative">
          <AnimatedDonut rate={rate} color={donutColor} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-6xl font-mono font-bold text-white">{animatedRate}%</span>
            <span className="text-sm text-gray-500 mt-2">cumplimiento</span>
          </div>
        </div>

        {/* KPI pills */}
        <div className="grid grid-cols-2 gap-3 w-full">
          <KPIPill label="Completados" value={metrics.controls.completed} color="#10b981" />
          <KPIPill label="En progreso" value={metrics.controls.inProgress} color="#3b82f6" />
          <KPIPill label="Críticos"    value={metrics.controls.critical}   color="#ef4444" />
          <KPIPill label="Tareas venc."value={metrics.tasks.overdue}       color="#f59e0b" />
        </div>

        {/* Task progress mini bar */}
        <div className="w-full">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Tareas completadas</span>
            <span>{taskRate}%</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden w-full">
            <div
              className="h-full bg-purple-500 rounded-full"
              style={{
                width: `${taskRate}%`,
                transition: 'width 2s ease-out',
              }}
            />
          </div>
        </div>
      </div>

      {/* Right — Category bars */}
      <div className="flex-1 flex flex-col gap-5">
        <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-1">
          Cumplimiento por dominio
        </h3>
        {categories.map((cat, i) => (
          <CategoryBar
            key={cat.label}
            label={cat.label}
            rate={cat.rate}
            total={cat.total}
            completed={cat.completed}
            color={CATEGORY_COLORS[i % CATEGORY_COLORS.length]}
            delay={300 + i * 150}
          />
        ))}
        {categories.length === 0 && (
          <p className="text-gray-600 text-base">Cargando métricas...</p>
        )}
      </div>
    </div>
  );
});

function KPIPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-xl px-5 py-4 text-center">
      <span className="text-3xl font-mono font-bold" style={{ color }}>{value}</span>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
    </div>
  );
}
